import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import { X, Trash2 } from 'lucide-react';
import { CarouselTemplate, carouselTemplateService } from '../../services/carouselTemplateService';

let domPurifyTemplateHooksInstalled = false;
const installTemplateDomPurifyHooks = () => {
  if (domPurifyTemplateHooksInstalled) return;
  domPurifyTemplateHooksInstalled = true;

  // Разрешаем <img> только с src из нашего S3 и чистим style на <img>
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const tagName = (node as any)?.tagName?.toUpperCase?.() || '';
    const attrName = (data as any)?.attrName || '';
    const attrValue = String((data as any)?.attrValue ?? '');

    if (tagName === 'IMG' && attrName === 'src') {
      if (!attrValue.startsWith('https://s3.twcstorage.ru/')) {
        (data as any).keepAttr = false;
      }
      return;
    }

    if (tagName === 'IMG' && attrName === 'style') {
      const allowedProps = new Set([
        'position',
        'top',
        'left',
        'right',
        'bottom',
        'z-index',
        'width',
        'height',
        'object-fit',
        'object-position',
        'border-radius',
        'border-top-left-radius',
        'border-top-right-radius',
        'border-bottom-left-radius',
        'border-bottom-right-radius',
        'transform',
        'opacity',
        'filter',
        'display',
        'flex',
        'flex-direction',
        'justify-content',
        'align-items',
        'margin',
        'margin-top',
        'margin-bottom',
        'margin-left',
        'margin-right',
        'padding',
        'padding-top',
        'padding-bottom',
        'padding-left',
        'padding-right',
        'border',
        'border-left',
        'border-right',
        'border-top',
        'border-bottom',
        'box-sizing',
        'background',
        'background-color',
        'background-image',
        'background-size',
        'background-position',
        'background-repeat',
        'font-family',
        'font-size',
        'font-weight',
        'line-height',
        'letter-spacing',
        'text-transform',
        'text-align',
        'overflow',
        'gap',
        'box-shadow',
        'color',
      ]);

      const safe = attrValue
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)
        .map((decl) => {
          const idx = decl.indexOf(':');
          if (idx <= 0) return null;
          const key = decl.slice(0, idx).trim().toLowerCase();
          const val = decl.slice(idx + 1).trim();
          if (!allowedProps.has(key)) return null;
          if (!val) return null;
          return `${key}: ${val}`;
        })
        .filter(Boolean)
        .join('; ');

      (data as any).attrValue = safe;
    }
  });
};

installTemplateDomPurifyHooks();

type Props = {
  open: boolean;
  selectedTemplateId?: string | null;
  onSelect: (template: CarouselTemplate) => void;
  onClose: () => void;
};

const domPurifyConfig: import('dompurify').Config = {
  ALLOWED_TAGS: [
    'div', 'span', 'p',
    'header', 'footer', 'main', 'section', 'article',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'strong', 'em', 'b', 'i', 'u', 's', 'small', 'mark',
    'sup', 'sub',
    'ul', 'ol', 'li',
    'code', 'pre', 'blockquote',
    'br', 'hr',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'img',
    // SVG элементы
    'svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'ellipse',
    'g', 'defs', 'use', 'text', 'tspan', 'textPath', 'clipPath', 'mask',
    'linearGradient', 'radialGradient', 'stop', 'pattern', 'image',
  ],
  ALLOWED_ATTR: [
    'style', 'src', 'alt',
    // SVG атрибуты
    'viewBox', 'xmlns', 'xmlns:xlink', 'width', 'height', 'x', 'y',
    'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-dashoffset',
    'd', 'cx', 'cy', 'r', 'rx', 'ry', 'points', 'x1', 'y1', 'x2', 'y2',
    'transform', 'opacity', 'fill-opacity', 'stroke-opacity',
    'clip-path', 'mask', 'id', 'class',
  ],
  FORBID_TAGS: [
    'script', 'style', 'link',
    'canvas',
    'video', 'audio', 'source',
    'iframe', 'object', 'embed',
    'form', 'input', 'textarea', 'select', 'option', 'button',
    'meta', 'base',
  ],
  FORBID_ATTR: ['on*', 'href', 'srcset'],
  RETURN_TRUSTED_TYPE: false
};

const extractFirstSlideHtml = (htmlJson?: string): string | null => {
  if (!htmlJson) return null;
  try {
    const parsed = JSON.parse(htmlJson);
    const arr = Array.isArray(parsed) ? parsed : [];
    const first = arr.find((x) => typeof x === 'string') as string | undefined;
    return first ?? null;
  } catch {
    return null;
  }
};

// Функция для определения формата карточки (квадратная 1080x1080 или вертикальная 1080x1350)
const detectCardFormat = (html: string): { width: number; height: number; isSquare: boolean } => {
  // Пытаемся найти размеры в стилях
  const widthRegex = /width\s*:\s*(\d+)px/gi;
  const heightRegex = /height\s*:\s*(\d+)px/gi;
  
  let width = 1080;
  let height = 1350;
  
  // Ищем ширину
  let match = widthRegex.exec(html);
  while (match) {
    const w = parseInt(match[1], 10);
    if (w === 1080) {
      width = w;
      break;
    }
    match = widthRegex.exec(html);
  }
  
  // Ищем высоту
  match = heightRegex.exec(html);
  while (match) {
    const h = parseInt(match[1], 10);
    if (h === 1080 || h === 1350) {
      height = h;
      break;
    }
    match = heightRegex.exec(html);
  }
  
  // Если нашли оба размера, определяем формат
  if (width === 1080 && (height === 1080 || height === 1350)) {
    const isSquare = height === 1080;
    return { width, height, isSquare };
  }
  
  // Если не нашли в стилях, создаем временный элемент для измерения
  try {
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.width = '1080px';
    tempDiv.style.height = 'auto';
    tempDiv.style.top = '-9999px';
    tempDiv.innerHTML = html;
    document.body.appendChild(tempDiv);
    
    // Ждем немного для рендеринга
    const naturalHeight = tempDiv.scrollHeight;
    document.body.removeChild(tempDiv);
    
    // Определяем формат по естественной высоте
    const isSquare = Math.abs(naturalHeight - 1080) < 50;
    return { width: 1080, height: isSquare ? 1080 : 1350, isSquare };
  } catch {
    // По умолчанию считаем вертикальной
    return { width: 1080, height: 1350, isSquare: false };
  }
};

const TemplateCard: React.FC<{
  template: CarouselTemplate;
  isSelected: boolean;
  onClick: () => void;
  onDelete?: () => void;
}> = ({ template, isSelected, onClick, onDelete }) => {
  const firstSlide = useMemo(() => extractFirstSlideHtml(template.htmlJson), [template.htmlJson]);
  const sanitized = useMemo(() => {
    if (!firstSlide) return '';
    return DOMPurify.sanitize(firstSlide, domPurifyConfig) as unknown as string;
  }, [firstSlide]);

  // Определяем формат карточки
  const cardFormat = useMemo(() => {
    if (sanitized) {
      return detectCardFormat(sanitized);
    }
    return { width: 1080, height: 1350, isSquare: false };
  }, [sanitized]);

  // миниатюра с учетом формата карточки
  const thumbWidth = 120;
  const thumbHeight = Math.round(thumbWidth * (cardFormat.height / cardFormat.width));
  const scale = thumbWidth / cardFormat.width;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Выбрать шаблон ${template.name}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      className={`group relative rounded-lg border p-2 cursor-pointer transition-all ${
        isSelected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-900'
      }`}
    >
      <div
        className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-black mx-auto"
        style={{ width: thumbWidth, height: thumbHeight }}
      >
        {sanitized ? (
          <div
            style={{
              width: cardFormat.width,
              height: cardFormat.height,
              transform: `scale(${scale})`,
              transformOrigin: 'top left'
            }}
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
            Нет превью
          </div>
        )}
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-1.5">
        <div className="min-w-0">
          <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
            {template.name}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {template.isGlobal ? 'Библиотека' : 'Мой шаблон'}
          </div>
        </div>

        {!template.isGlobal && onDelete && (
          <button
            type="button"
            aria-label="Удалить шаблон"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-500 hover:text-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export const CarouselTemplateSelector: React.FC<Props> = ({ open, selectedTemplateId, onSelect, onClose }) => {
  const [tab, setTab] = useState<'mine' | 'library'>('mine');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<CarouselTemplate[]>([]);

  const mine = useMemo(() => items.filter((t) => !t.isGlobal), [items]);
  const library = useMemo(() => items.filter((t) => t.isGlobal), [items]);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    setError(null);
    carouselTemplateService
      .getTemplates()
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch((e) => setError(String((e as any)?.message || e || 'Не удалось загрузить шаблоны')))
      .finally(() => setIsLoading(false));
  }, [open]);

  const list = tab === 'mine' ? mine : library;

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[25000]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-label="Закрыть" />

      <div className="absolute inset-0 flex items-center justify-center p-3">
        <div className="w-full max-w-4xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden max-h-[calc(100vh-24px)] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Шаблоны</div>

              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => setTab('mine')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    tab === 'mine'
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Мои
                </button>
                <button
                  type="button"
                  onClick={() => setTab('library')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    tab === 'library'
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Библиотека
                </button>
              </div>
            </div>

            <button
              type="button"
              aria-label="Закрыть"
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 overflow-auto">
            {isLoading && (
              <div className="text-sm text-gray-600 dark:text-gray-300">Загружаем шаблоны…</div>
            )}
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            )}

            {!isLoading && !error && list.length === 0 && (
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {tab === 'mine'
                  ? 'У вас пока нет сохранённых шаблонов. Откройте карусель → вкладка «Карусели» → «Сохранить как шаблон».'
                  : 'В библиотеке пока нет шаблонов.'}
              </div>
            )}

            {!isLoading && !error && list.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5">
                {list.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    isSelected={t.id === selectedTemplateId}
                    onClick={() => onSelect(t)}
                    onDelete={
                      t.isGlobal
                        ? undefined
                        : async () => {
                            const ok = window.confirm(`Удалить шаблон «${t.name}»?`);
                            if (!ok) return;
                            try {
                              await carouselTemplateService.deleteTemplate(t.id);
                              setItems((prev) => prev.filter((x) => x.id !== t.id));
                            } catch (e) {
                              window.alert(String((e as any)?.message || e || 'Не удалось удалить шаблон'));
                            }
                          }
                    }
                  />
                ))}
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 text-sm rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};


